import { Box, Typography } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import maxime from "../assets/img/patient-min.png";
import bebe from "../assets/newImg/bebe.png";
import drainage from "../assets/newImg/drainage lumpthatique.png";
import enceinte from "../assets/newImg/femme enceinte.png";
import ligneDeVie from "../assets/newImg/ligneDeVie.png";
import CabinetsMap from "../components/CabinetsMap";
import SEO, { SITE_URL } from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const STEPS = [
  {
    num: "01",
    title: "Prise de rendez-vous",
    desc: "En ligne sur Doctolib 24h/24, ou par téléphone au 06 95 78 89 83. Consultation du lundi au samedi.",
  },
  {
    num: "02",
    title: "Anamnèse & examen",
    desc: "Échange sur vos antécédents, vos douleurs et vos objectifs. Bilan postural et palpatoire complet.",
  },
  {
    num: "03",
    title: "Soin ostéopathique",
    desc: "Traitement manuel adapté à votre situation : techniques structurelles, viscérales ou crâniennes.",
  },
  {
    num: "04",
    title: "Conseils & suivi",
    desc: "Recommandations personnalisées sur la posture, l'activité et les gestes du quotidien.",
  },
];

const AVIS = [
  {
    stars: "★★★★★",
    text: "Consulté en urgence pour un dos complètement bloqué, j'ai été soulagé dès la première séance. Ostéopathe très à l'écoute, efficace et rassurant, il a su non seulement débloquer la situation mais aussi me donner de précieux conseils pour la suite. Je recommande sans hésitation !",
    author: "Camille A.",
  },
  {
    stars: "★★★★★",
    text: "Maxime est un professionnel très à l'écoute, il a su entendre toutes les problématiques et agir en fonction de mes douleurs. Très disponible, ses conseils sont précis et adaptés. Je recommande vivement ce professionnel qui prend en compte tous les besoins.",
    author: "Victoire L.",
  },
  {
    stars: "★★★★★",
    text: "Très compétent ! Maxime est souriant, avenant, on se sent de suite à l'aise. Il a une méthode plutôt douce et efficace ! Je recommande à 100%",
    author: "Elodie",
  },
];

const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["MedicalBusiness", "LocalBusiness"],
  name: "Maxime Nicolaieff Ostéopathe D.O.",
  image: `${SITE_URL}/og-image.webp`,
  "@id": SITE_URL,
  url: SITE_URL,
  telephone: "+33695788983",
  email: "nicolaieffmaxime@gmail.com",
  priceRange: "50€–100€",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: 69,
  },
  address: [
    {
      "@type": "PostalAddress",
      streetAddress: "8 Route de Mons",
      addressLocality: "Balma",
      postalCode: "31130",
      addressCountry: "FR",
    },
    {
      "@type": "PostalAddress",
      streetAddress: "164 Allée du Camp del Rey",
      addressLocality: "Castelnau-d'Estrétefonds",
      postalCode: "31620",
      addressCountry: "FR",
    },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      opens: "08:00",
      closes: "20:00",
    },
  ],
  sameAs: ["https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"],
};

const PERSON_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Maxime Nicolaieff",
  jobTitle: "Ostéopathe D.O.",
  telephone: "+33695788983",
  email: "nicolaieffmaxime@gmail.com",
  url: `${SITE_URL}/votre-osteopathe`,
  worksFor: { "@id": SITE_URL },
};

const Accueil = () => (
  <>
    <SEO
      title="Ostéopathe à Balma &amp; Castelnau  Maxime Nicolaieff D.O."
      description="Maxime Nicolaieff, ostéopathe D.O. à Balma (31130) et Castelnau-d'Estrétefonds (31620). Consultations pour adultes, nourrissons, femmes enceintes et sportifs. RDV en ligne sur Doctolib."
      path="/"
      schema={[LOCAL_BUSINESS_SCHEMA, PERSON_SCHEMA]}
    />
    {/* ── Hero ─────────────────────────────────────────────── */}
    <Box
      component="section"
      sx={{ pt: "68px", backgroundColor: C.bg, overflow: "hidden" }}
    >
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          pt: { xs: "3.5rem", md: "5rem" },
          pb: { xs: "2.5rem", md: "4rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 420px" },
          gap: "3rem",
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={{ ...SX.sectionLabel, mb: "1rem" }}>
            Ostéopathe D.O. Balma &amp; Castelnau
          </Box>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(2.2rem, 3.8vw, 4rem)",
              fontWeight: 300,
              lineHeight: 1.1,
              color: C.text,
              mb: "1.25rem",
              "& em": { fontStyle: "italic", color: C.accent },
            }}
          >
            Retrouver son <em>équilibre,</em>
            <br />
            en douceur.
          </Typography>
          <Typography
            sx={{
              fontSize: ".95rem",
              color: C.textMuted,
              maxWidth: "440px",
              lineHeight: 1.75,
              mb: "2rem",
            }}
          >
            Maxime Nicolaieff vous reçoit dans deux cabinets en région
            toulousaine et se déplace à domicile dans un rayon de 20 km. Du
            nourrisson à la personne âgée.
          </Typography>
          <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              sx={SX.btnDark}
            >
              Prendre rendez-vous
            </Box>
            <Box component={Link} to="/votre-osteopathe" sx={SX.btnOutline}>
              En savoir plus
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: "2.5rem",
              mt: "3rem",
              pt: "2rem",
              borderTop: `1px solid ${C.border}`,
              flexWrap: "wrap",
              "@media (max-width: 640px)": { gap: "1.5rem" },
            }}
          >
            {[
              { value: "2019", label: "En exercice" },
              { value: "2", label: "Cabinets" },
              { value: "4,9 ★", label: "sur Google" },
            ].map((s) => (
              <Box key={s.label}>
                <Typography
                  sx={{
                    fontFamily: F.display,
                    fontSize: "1.9rem",
                    fontWeight: 400,
                    color: C.text,
                    lineHeight: 1,
                  }}
                >
                  {s.value}
                </Typography>
                <Typography
                  sx={{ fontSize: ".72rem", color: C.textMuted, mt: ".25rem" }}
                >
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{ position: "relative", display: { xs: "none", lg: "block" } }}
        >
          <Box
            sx={{
              width: "380px",
              height: "380px",
              borderRadius: "50%",
              overflow: "hidden",
              mx: "auto",
              boxShadow: S.lg,
            }}
          >
            <Box
              component="img"
              src={maxime}
              alt="Maxime Nicolaieff, ostéopathe"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        </Box>
      </Box>
    </Box>

    {/* ── Spécialités ──────────────────────────────────────── */}
    <Box component="section" sx={{ py: { xs: "2.5rem", md: "5rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: "2.5rem",
            gap: "1.5rem",
            "@media (max-width: 640px)": {
              flexDirection: "column",
              alignItems: "flex-start",
            },
          }}
        >
          <Box>
            <Box component="span" sx={SX.sectionLabel}>
              Expertise
            </Box>
            <Typography
              variant="h2"
              component="h2"
              sx={{ ...SX.displayTitle, maxWidth: "600px" }}
            >
              Une expertise particulière pour les <em>moments délicats.</em>
            </Typography>
          </Box>
          <Box
            component={Link}
            to="/pathologies"
            sx={{
              fontSize: ".82rem",
              whiteSpace: "nowrap",
              color: C.textMuted,
              textDecoration: "none",
              "&:hover": { color: C.accent },
              "&::after": { content: '" →"' },
            }}
          >
            Toutes les pathologies
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: "1.25rem",
          }}
        >
          {[
            {
              src: bebe,
              alt: "Pédiatrie et nourrissons",
              tag: "Pédiatrie",
              title: "Pédiatrie & nourrissons",
              desc: "Accueil doux et adapté dès les premiers jours de vie.",
              to: "/pathologies/nourrissons",
              imgPos: "center 25%",
            },
            {
              src: enceinte,
              alt: "Femmes enceintes",
              tag: "Prénatal",
              title: "Femmes enceintes",
              desc: "Accompagnement trimestre par trimestre, avant et après l'accouchement.",
              to: "/pathologies/femme-enceinte",
            },
            {
              src: drainage,
              alt: "Drainage lymphatique",
              tag: "Bien-être",
              title: "Drainage lymphatique",
              desc: "Méthode Vodder pour drainer, détoxifier et soulager les tensions.",
              to: "/pathologies/drainage-lymphatique",
            },
          ].map((card) => (
            <Box
              key={card.title}
              component={card.to ? Link : "div"}
              to={card.to}
              sx={{
                borderRadius: R.lg,
                overflow: "hidden",
                position: "relative",
                height: "300px",
                cursor: card.to ? "pointer" : "default",
                textDecoration: "none",
                display: "block",
                "&:hover img": { transform: "scale(1.04)" },
              }}
            >
              <Box
                component="img"
                src={card.src}
                alt={card.alt}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: card.imgPos || "center",
                  transition: "transform .5s ease",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(17,29,20,.75) 0%, transparent 55%)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  padding: "1.5rem",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: ".62rem",
                    fontWeight: 600,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    color: C.accent,
                    mb: ".4rem",
                  }}
                >
                  {card.tag}
                </Box>
                <Typography
                  variant="h3"
                  component="h3"
                  sx={{
                    fontFamily: F.display,
                    fontSize: "1.3rem",
                    fontWeight: 400,
                    color: "#fff",
                    mb: ".3rem",
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".78rem",
                    color: "rgba(255,255,255,.7)",
                    lineHeight: 1.5,
                  }}
                >
                  {card.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── À chaque étape ───────────────────────────────────── */}
    <Box
      component="section"
      sx={{
        backgroundColor: "#f3efe8",
        pt: { xs: "2.5rem", md: "4rem" },
        pb: { xs: "2rem", md: "3rem" },
      }}
    >
      {/* Titre au-dessus */}
      <Box sx={{ textAlign: "center", mb: { xs: "1.5rem", md: "2rem" } }}>
        <Box component="span" sx={SX.sectionLabel}>
          Pour tous
        </Box>
        <Typography variant="h2" component="h2" sx={SX.displayTitle}>
          Pour tous, à <em>chaque étape</em> de la vie.
        </Typography>
      </Box>

      {/* Image pleine largeur */}
      <Box
        component="img"
        src={ligneDeVie}
        alt="De la naissance à la personne âgée  ostéopathie pour tous"
        sx={{ display: "block", maxWidth: "100%", mx: "auto", height: "auto" }}
      />

      {/* Étiquettes — desktop uniquement */}
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          mt: { xs: "1.25rem", md: "1.75rem" },
          display: { xs: "none", md: "flex" },
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        {[
          { label: "Nourrissons & Pédiatrie", desc: "Grandir en pleine santé" },
          { label: "Enfants & Adolescents", desc: "Bien se développer" },
          {
            label: "Femme enceinte & Post-partum",
            desc: "Vivre pleinement la maternité",
          },
          {
            label: "Sportifs & Performance",
            desc: "S'épanouir, en toute sécurité",
          },
          { label: "Seniors", desc: "Bien vieillir, sainement" },
        ].map((item) => (
          <Box key={item.label} sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: ".8rem",
                fontWeight: 600,
                color: "#3a3530",
                mb: ".15rem",
              }}
            >
              {item.label}
            </Typography>
            <Typography sx={{ fontSize: ".7rem", color: "#7a7068" }}>
              {item.desc}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Texte synthétique — mobile uniquement */}
      <Typography
        sx={{
          display: { xs: "block", md: "none" },
          textAlign: "center",
          fontSize: ".85rem",
          color: "#7a7068",
          px: "1.5rem",
          mt: "1.25rem",
          lineHeight: 1.6,
        }}
      >
        Nourrissons, enfants, adolescents, femmes enceintes, sportifs et
        seniors — l'ostéopathie accompagne chaque étape de la vie.
      </Typography>
    </Box>

    {/* ── Consultation en 4 temps ──────────────────────────── */}
    <Box component="section" sx={{ py: "6rem" }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
          gap: { xs: "3rem", md: "5rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Déroulement
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1rem" }}
          >
            Une consultation, <em>quatre temps.</em>
          </Typography>
          <Typography
            sx={{ fontSize: ".9rem", color: C.textMuted, lineHeight: 1.75 }}
          >
            Chaque séance dure entre 45 minutes et 1 heure. Elle se déroule dans
            un espace calme, propice à l'écoute et au soin.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          {STEPS.map((s) => (
            <Box
              key={s.num}
              sx={{
                display: "grid",
                gridTemplateColumns: "48px 1fr",
                gap: "1.25rem",
                alignItems: "start",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.8rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                  pt: ".1rem",
                }}
              >
                {s.num}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".95rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".35rem",
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {s.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Avis ─────────────────────────────────────────────── */}
    <Box
      component="section"
      sx={{ backgroundColor: C.dark, py: { xs: "3rem", md: "5.5rem" } }}
    >
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: "3rem",
            gap: "2rem",
            "@media (max-width: 640px)": {
              flexDirection: "column",
              alignItems: "flex-start",
            },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.15,
            }}
          >
            + 69 avis qui parlent
            <br />
            mieux que moi.
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: ".75rem",
              flexShrink: 0,
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: F.display,
                fontSize: "3rem",
                fontWeight: 300,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              5,0
            </Box>
            <Box>
              <Box
                sx={{
                  color: "#F5C518",
                  fontSize: "1.1rem",
                  letterSpacing: "2px",
                }}
              >
                ★★★★★
              </Box>
              <Box sx={{ fontSize: ".72rem", color: "rgba(255,255,255,.4)" }}>
                sur Google
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: "1.25rem",
          }}
        >
          {AVIS.map((a, i) => (
            <Box
              key={i}
              sx={{
                background: "rgba(255,255,255,.06)",
                border: "1px solid rgba(255,255,255,.1)",
                borderRadius: R.base,
                padding: "1.5rem",
              }}
            >
              <Box sx={{ color: "#F5C518", fontSize: ".8rem", mb: ".75rem" }}>
                {a.stars}
              </Box>
              <Typography
                sx={{
                  fontSize: ".82rem",
                  color: "rgba(255,255,255,.72)",
                  lineHeight: 1.65,
                  mb: "1rem",
                  fontStyle: "italic",
                }}
              >
                « {a.text} »
              </Typography>
              <Typography
                sx={{
                  fontSize: ".75rem",
                  fontWeight: 600,
                  color: "rgba(255,255,255,.5)",
                }}
              >
                {a.author}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ textAlign: "center", mt: "2.5rem" }}>
          <Box
            component="a"
            href="https://www.google.com/search?q=Maxime+Nicolaieff+osteopathe+Balma"
            target="_blank"
            rel="noreferrer"
            sx={{
              color: "rgba(255,255,255,.5)",
              fontSize: ".82rem",
              textDecoration: "none",
              transition: `color ${trans}`,
              "&:hover": { color: "rgba(255,255,255,.85)" },
              "&::after": { content: '" →"' },
            }}
          >
            Voir tous les avis Google
          </Box>
        </Box>
      </Box>
    </Box>

    {/* ── Deux adresses ─────────────────────────────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5.5rem" } }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: "4rem",
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Cabinets
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "2rem" }}
          >
            Deux adresses, <em>à 30 km</em> entre les deux.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
            {[
              {
                name: "As Cambiots Balma",
                addr: "8 Route de Mons, 31130 Balma",
              },
              {
                name: "Maison Aphéléia Castelnau-d'Estrétefonds",
                addr: "164 Allée du Camp del Rey, 31620 Castelnau-d'Estrétefonds",
              },
            ].map((cab) => (
              <Box
                key={cab.name}
                sx={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: C.accent,
                    flexShrink: 0,
                    mt: ".35rem",
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: ".92rem",
                      fontWeight: 600,
                      color: C.text,
                      mb: ".2rem",
                    }}
                  >
                    {cab.name}
                  </Typography>
                  <Typography sx={{ fontSize: ".82rem", color: C.textMuted }}>
                    {cab.addr}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: "2rem" }}>
            <Box component={Link} to="/cabinets" sx={SX.btnDark}>
              Voir les cabinets
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: { xs: "240px", md: "320px" },
            borderRadius: R.lg,
            overflow: "hidden",
            boxShadow: S.md,
            background: C.bgAlt,
            "& iframe": {
              width: "100%",
              height: "100%",
              border: 0,
              display: "block",
            },
          }}
        >
          <CabinetsMap />
        </Box>
      </Box>
    </Box>
  </>
);

export default Accueil;
